import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";

export interface SectionPerformance {
  name: string;
  originalName?: string;
  correct: number;
  incorrect: number;
  skipped: number;
  score: number;
  maxScore: number;
  percentage: number;
  passMark?: number;
  isPassed?: boolean;
}

export interface ExamResultReportProps {
  examName: string;
  examTypeLabel: string;
  candidateId: string;
  candidateName: string;
  completionDate: Date;
  
  isPassed: boolean;
  totalScore: number;
  obtainedScore: number;
  percentage: number;
  passingScore: number;
  
  timeTakenMinutes: number | null;
  totalQuestions: number;
  answeredCount: number;
  skippedCount: number;
  overallAccuracy: number;
  
  sections: SectionPerformance[];
  failureNotice?: string | null;
  actions: ReactNode;
}

export function ExamResultReport({
  examName,
  examTypeLabel,
  candidateId,
  candidateName,
  completionDate,
  
  isPassed,
  totalScore,
  obtainedScore,
  percentage,
  passingScore,
  
  timeTakenMinutes,
  totalQuestions,
  answeredCount,
  skippedCount,
  overallAccuracy,
  
  sections,
  failureNotice,
  actions,
}: ExamResultReportProps) {
  
  const isJFT = examTypeLabel.toUpperCase().includes("JFT");

  return (
    <div className="space-y-6">
      {/* Results sheet certificate header */}
      <div className="relative border-b border-stone-200 pb-6 mb-6 select-none flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="text-center md:text-left space-y-1.5">
            <h3 className="text-2xl font-bold tracking-widest text-stone-900">
                合否結果通知書
            </h3>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-sans font-semibold">
                {isJFT ? "JFT-BASIC REPORT OF SCORE" : "MOCK TEST REPORT OF SCORE"}
            </p>
            <div className="h-0.5 w-20 bg-stone-900 mt-1" />
        </div>

        {/* Official Stamp Badge */}
        <div className={cn(
            "w-24 h-24 rounded-full border-4 flex flex-col items-center justify-center font-bold tracking-tighter uppercase select-none opacity-90 rotate-6 shrink-0 relative",
            isPassed
                ? "border-emerald-600 text-emerald-600 bg-emerald-50/10 shadow-emerald-100/30 shadow-md"
                : "border-rose-600 text-rose-600 bg-rose-50/10 shadow-rose-100/30 shadow-md"
        )}>
            <div className="text-[9px] leading-none font-sans">
                {isPassed ? "PASS" : "FAIL"}
            </div>
            <div className="text-xl leading-none tracking-widest my-0.5">
                {isPassed ? "合格" : "不合格"}
            </div>
            <div className="text-[8px] leading-none font-sans font-medium">
                {isPassed ? "APPROVED" : "REJECTED"}
            </div>
            <div className={cn(
                "absolute inset-1 rounded-full border border-dashed",
                isPassed ? "border-emerald-600/60" : "border-rose-600/60"
            )} />
        </div>
      </div>

      {/* Warning / Notice about section failure if applicable */}
      {failureNotice && (
          <div className="border border-rose-200 bg-rose-50/50 text-rose-800 p-4 rounded-xl text-xs flex items-start gap-3 select-none mb-6 font-sans">
              <AlertCircle className="size-4 shrink-0 text-rose-600 mt-0.5" />
              <div>
                  <h4 className="font-bold text-rose-900 mb-0.5">セクション別基準点未達による不合格 / Sectional Pass Mark Fail</h4>
                  <p className="leading-relaxed">
                      {failureNotice}
                  </p>
              </div>
          </div>
      )}

      {/* Scorecard Metadata Header */}
      <div className="border border-stone-250 p-6 space-y-4 select-none bg-stone-50/50 rounded-xl mb-6 font-sans relative overflow-hidden">
          <div className="flex flex-wrap gap-x-8 gap-y-4 text-xs text-stone-700">
              <div>
                  <span className="text-stone-500 block text-[9px] uppercase font-bold tracking-wider mb-1 whitespace-nowrap">受験番号 / Candidate ID</span>
                  <strong className="text-stone-900 text-sm font-mono">{candidateId.slice(0, 8).toUpperCase()}</strong>
              </div>
              <div>
                  <span className="text-stone-500 block text-[9px] uppercase font-bold tracking-wider mb-1 whitespace-nowrap">氏名 / Candidate Name</span>
                  <strong className="text-stone-900 text-sm">{candidateName}</strong>
              </div>
              <div>
                  <span className="text-stone-500 block text-[9px] uppercase font-bold tracking-wider mb-1 whitespace-nowrap">実施日 / Examination Date</span>
                  <strong className="text-stone-900 text-sm">
                      {completionDate.toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" })}
                  </strong>
              </div>
          </div>
      </div>

      {/* Score Analysis Card Panels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6 select-none font-sans">
          {/* Total Score Card */}
          <div className="md:col-span-1 border border-stone-300 p-6 rounded-xl bg-stone-900 text-white flex flex-col justify-between shadow-sm">
              <div>
                  <span className="text-[9px] uppercase tracking-wider text-stone-400 block font-bold">総合得点 / Total Score</span>
                  <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="text-4xl font-extrabold">{obtainedScore}</span>
                      <span className="text-stone-400 text-sm">/ {totalScore} 点</span>
                  </div>
                  <div className="mt-2 text-[10px] text-stone-400">
                      合格基準点 / Pass Mark: <span className="text-white font-mono">{passingScore} 点</span>
                  </div>
              </div>
              <div className="mt-5 pt-4 border-t border-stone-850 flex flex-col gap-4 text-xs">
                  <div>
                      <span className="text-stone-400 block mb-1">得点率 / Percentage:</span>
                      <span className="font-bold text-xl text-amber-400">{percentage}%</span>
                  </div>
                  <div>
                      <span className="text-stone-400 block mb-1">総合判定 / Verdict:</span>
                      <span className={cn(
                          "inline-block font-bold text-xs uppercase px-2 py-1 rounded",
                          isPassed ? "bg-emerald-950/60 text-emerald-400 border border-emerald-800" : "bg-rose-950/60 text-rose-400 border border-rose-800"
                      )}>
                          {isPassed ? "PASS / 合格" : "FAIL / 不合格"}
                      </span>
                  </div>
              </div>
          </div>

          {/* Section Breakdown Analysis cards */}
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sections.length > 0 ? sections.map((sec, i) => (
                  <div key={i} className="border border-stone-200 p-5 rounded-xl bg-white shadow-xs flex flex-col justify-between gap-4">
                      {/* Title and Badge */}
                      <div className="flex flex-col items-start gap-2">
                          <div className="text-sm font-semibold text-stone-850 leading-snug">
                              {sec.name}
                          </div>
                          {!isJFT && sec.isPassed !== undefined && (
                              <span className={cn(
                                  "text-[10px] px-2.5 py-1 rounded-md font-bold uppercase whitespace-nowrap",
                                  sec.isPassed
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                      : "bg-rose-50 text-rose-700 border border-rose-200"
                              )}>
                                  {sec.isPassed ? "PASS / 合格" : "FAIL / 不合格"}
                              </span>
                          )}
                      </div>

                      {/* Correct / Incorrect / Skipped breakdown */}
                      {(sec.correct + sec.incorrect + sec.skipped) > 0 && (
                          <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="bg-emerald-50 border border-emerald-200 rounded-lg py-2 px-1">
                                  <div className="text-lg font-bold font-mono text-emerald-700 leading-none">{sec.correct}</div>
                                  <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-600 mt-1">正解</div>
                              </div>
                              <div className="bg-rose-50 border border-rose-200 rounded-lg py-2 px-1">
                                  <div className="text-lg font-bold font-mono text-rose-700 leading-none">{sec.incorrect}</div>
                                  <div className="text-[9px] font-semibold uppercase tracking-wider text-rose-600 mt-1">不正解</div>
                              </div>
                              <div className="bg-stone-50 border border-stone-200 rounded-lg py-2 px-1">
                                  <div className="text-lg font-bold font-mono text-stone-500 leading-none">{sec.skipped}</div>
                                  <div className="text-[9px] font-semibold uppercase tracking-wider text-stone-400 mt-1">未解答</div>
                              </div>
                          </div>
                      )}

                      {/* Stats Row */}
                      <div className="mt-auto space-y-3 pt-2">
                          <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs font-semibold">
                              <div className="text-stone-500">
                                  得点 / Score: <span className="text-stone-900 font-mono text-sm ml-0.5">{sec.score}</span>
                              </div>
                              <div className="text-stone-500 text-right">
                                  満点 / Max: <span className="text-stone-900 font-mono ml-0.5">{sec.maxScore}</span>
                              </div>
                              
                              <div className="col-span-2 flex justify-between items-end mt-1">
                                  <div className="text-[11px] text-stone-400 font-normal">
                                      {!isJFT && sec.passMark !== undefined ? (
                                          <>基準点 / Pass Mark: <span className="text-stone-600 font-mono ml-0.5">{sec.passMark}</span></>
                                      ) : null}
                                  </div>
                                  <div className={cn("text-right font-bold text-lg leading-none", isJFT ? "text-slate-700" : (sec.isPassed !== false ? "text-emerald-600" : "text-rose-600"))}>
                                      {sec.percentage}%
                                  </div>
                              </div>
                          </div>

                          <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden">
                              <div
                                  className={cn(
                                      "h-full rounded-full transition-all duration-300",
                                      isJFT ? "bg-slate-700" : (sec.isPassed !== false ? "bg-emerald-600" : "bg-rose-600")
                                  )}
                                  style={{ width: `${Math.max(sec.percentage, 1)}%` }}
                              />
                          </div>
                      </div>
                  </div>

              )) : (
                  <div className="col-span-2 border border-dashed border-stone-200 rounded-xl p-8 flex items-center justify-center text-stone-400 text-xs">
                      No section data available
                  </div>
              )}
          </div>
      </div>

      {/* Enhanced Buttons Row */}
      <div className="font-sans select-none mt-8 space-y-4">
          {/* Stat pills: answered / unanswered / accuracy */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                  { label: "解答済 / Answered", value: `${answeredCount}`, sub: `out of ${totalQuestions}`, color: "text-stone-900" },
                  { label: "未解答 / Skipped", value: `${skippedCount}`, sub: "questions", color: skippedCount > 0 ? "text-rose-600" : "text-stone-900" },
                  { label: "正解率 / Accuracy", value: `${overallAccuracy}%`, sub: `${sections.reduce((acc, s) => acc + s.correct, 0)} correct`, color: overallAccuracy >= 60 ? "text-emerald-600" : "text-rose-600" },
                  { label: "所要時間 / Time", value: timeTakenMinutes !== null ? `${timeTakenMinutes} 分` : "—", sub: "minutes taken", color: "text-stone-900" },
              ].map(({ label, value, sub, color }) => (
                  <div key={label} className="border border-stone-200 rounded-xl px-4 py-3 bg-white text-center shadow-xs">
                      <div className="text-[9px] uppercase tracking-widest text-stone-400 font-bold mb-1">{label}</div>
                      <div className={`text-xl font-bold font-mono ${color}`}>{value}</div>
                      <div className="text-[9px] text-stone-400 mt-0.5">{sub}</div>
                  </div>
              ))}
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
              {actions}
          </div>
      </div>
    </div>
  );
}
